import {
  INVOICE_STATUSES,
  INVOICE_STATUS_LABELS,
  PAYMENT_METHODS,
  PROVIDER_COSTS,
  type AiCostsResponse,
  type BillingDetails,
  type Client,
  type Invoice,
  type MarginsResponse,
  type PaymentDetail,
  type Plan,
  type PlanAddon,
  type SubscriptionDetail,
  type Tenant,
} from '@platform/contracts';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  IconBadge,
  Input,
  Label,
  Monogram,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@platform/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  Ban,
  Cpu,
  Download,
  FileText,
  HandCoins,
  Landmark,
  Paperclip,
  PauseCircle,
  PlayCircle,
  Plus,
  PlusCircle,
  Repeat,
  Send,
  Trash2,
  TrendingDown,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import { type SubmitEvent, useId, useState } from 'react';
import { toast } from 'sonner';

import { Field, fd } from '../components/form';
import { ConfirmDialog, RowActions } from '../components/row-actions';
import { InvoiceBadge } from '../components/status-badges';
import { Loadable } from '../components/states';
import { api, errorMessage } from '../lib/api';
import { fmtNum } from '../lib/console';
import { fileToBase64, fmtDate, fmtPeriod } from '../lib/format';
import { deltaText, readLimits } from '../lib/limits';
import {
  DataTable,
  FormDialog,
  LimitFields,
  METHOD_ICON,
  NativeSelect,
  PlansSection,
  money,
  useAdminList,
  useAdminMutation,
} from './superadmin';

// ------------------------------------------------------------------ add-ons

export function AddonsSection() {
  const addons = useAdminList<PlanAddon>('addons', '/api/admin/addons');
  const run = useAdminMutation(['addons'], 'Add-on guardado');
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <IconBadge tone="navy">
            <PlusCircle />
          </IconBadge>
          <div>
            <h2 className="text-lg leading-tight font-semibold">Add-ons</h2>
            <p className="text-sm text-muted-foreground">
              Extras que se suman a los límites del plan de un cliente.
            </p>
          </div>
        </div>
        <FormDialog
          title="Nuevo add-on"
          wide
          trigger={
            <Button variant="outline">
              <Plus /> Nuevo add-on
            </Button>
          }
          onSubmit={(f) =>
            run(() =>
              api('/api/admin/addons', {
                method: 'POST',
                body: {
                  code: fd(f, 'code'),
                  name: fd(f, 'name'),
                  priceCents: Math.round(Number(fd(f, 'price')) * 100),
                  currency: fd(f, 'currency'),
                  limitsDelta: readLimits(f),
                },
              }),
            )
          }
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Código" name="code" placeholder="extra-1000-leads" required />
            <Field label="Nombre" name="name" placeholder="+1000 leads/mes" required />
            <Field label="Precio" name="price" type="number" step="0.01" min="0" required />
            <NativeSelect
              label="Moneda"
              name="currency"
              options={['EUR', 'USD', 'ARS'].map((c) => ({ value: c, label: c }))}
            />
          </div>
          <LimitFields value={{ leads_ingested_per_month: 1000 }} allowUnlimited={false} />
        </FormDialog>
      </div>
      <DataTable
        rows={addons.data}
        empty="Sin add-ons."
        emptyIcon={<PlusCircle />}
        columns={[
          {
            header: 'Add-on',
            cell: (a) => (
              <div>
                <p className="font-medium">{a.name}</p>
                <code className="text-xs text-muted-foreground">{a.code}</code>
              </div>
            ),
          },
          {
            header: 'Precio',
            cell: (a) => (
              <span className="font-medium tabular-nums">
                {money(a.priceCents, a.currency)} / {a.interval === 'year' ? 'año' : 'mes'}
              </span>
            ),
          },
          { header: 'Suma', cell: (a) => deltaText(a.limitsDelta) },
          {
            header: 'Estado',
            cell: (a) =>
              a.archivedAt ? (
                <Badge variant="secondary">Archivado</Badge>
              ) : (
                <Badge variant="success">Disponible</Badge>
              ),
          },
          {
            header: '',
            className: 'w-32 text-right',
            cell: (a) => (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  void run(() =>
                    api(`/api/admin/addons/${a.id}`, {
                      method: 'PATCH',
                      body: { archived: !a.archivedAt },
                    }),
                  ).catch((err: unknown) => toast.error(errorMessage(err)));
                }}
              >
                {a.archivedAt ? <ArchiveRestore /> : <Archive />}
                {a.archivedAt ? 'Reactivar' : 'Archivar'}
              </Button>
            ),
          },
        ]}
      />
    </div>
  );
}

/** Planes + add-ons en la misma sección. */
export function PlansAndAddonsSection() {
  return (
    <div className="flex flex-col gap-10">
      <PlansSection />
      <AddonsSection />
    </div>
  );
}

// ------------------------------------------------------------------ acciones de una suscripción

type SubDialog = 'plan' | 'addon' | 'suspend' | 'cancel' | null;

/**
 * Acciones de una suscripción: la principal a la vista (según el estado) y el resto en "⋯". Las que
 * cortan el servicio (suspender, cancelar) piden confirmación y, suspender, un motivo.
 */
export function SubscriptionBillingPanel({ subscriptionId }: { subscriptionId: string }) {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<SubDialog>(null);
  const details = useAdminList<SubscriptionDetail>(
    'subscription-details',
    '/api/admin/subscriptions/details',
  );
  const plans = useAdminList<Plan>('plans', '/api/admin/plans');
  const addons = useAdminList<PlanAddon>('addons', '/api/admin/addons');
  const sub = details.data?.find((s) => s.id === subscriptionId);
  const refresh = () =>
    Promise.all(
      ['subscription-details', 'subscriptions', 'invoices'].map((k) =>
        qc.invalidateQueries({ queryKey: ['admin', k] }),
      ),
    );
  /** Para acciones sueltas: avisa y no relanza. */
  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
      await refresh();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  /** Para diálogos: relanza, así el error queda en el diálogo. */
  const actOrThrow = async (fn: () => Promise<unknown>, ok: string) => {
    await fn();
    toast.success(ok);
    await refresh();
  };
  if (!sub) return <span className="text-xs text-muted-foreground">…</span>;
  const base = `/api/admin/subscriptions/${sub.id}`;
  const live = sub.status !== 'cancelled';
  const canSuspend =
    sub.status === 'past_due' || sub.status === 'active' || sub.status === 'trialing';
  const canReactivate = sub.status === 'suspended' || sub.status === 'past_due';
  const invoice = () =>
    act(
      () => api('/api/admin/invoices', { method: 'POST', body: { subscriptionId: sub.id } }),
      'Borrador de factura creado',
    );
  const reactivate = () =>
    act(
      () => api(`${base}/action`, { method: 'POST', body: { action: 'reactivate' } }),
      'Reactivada',
    );
  const activeAddons = sub.addons.filter((a) => !a.endedAt);
  return (
    <>
      <RowActions
        label={`la suscripción ${sub.id.slice(0, 8)}`}
        primary={
          sub.status === 'suspended' ? (
            <Button size="sm" onClick={() => void reactivate()}>
              <PlayCircle /> Reactivar
            </Button>
          ) : live ? (
            <Button size="sm" variant="outline" onClick={() => void invoice()}>
              <FileText /> Generar factura
            </Button>
          ) : null
        }
        items={
          live
            ? [
                {
                  label: 'Cambiar de plan',
                  icon: Repeat,
                  onSelect: () => {
                    setDialog('plan');
                  },
                },
                {
                  label: 'Agregar add-on',
                  icon: PlusCircle,
                  onSelect: () => {
                    setDialog('addon');
                  },
                },
                ...activeAddons.map((a) => ({
                  label: `Quitar ${a.addonName}`,
                  icon: Trash2,
                  onSelect: () => {
                    void act(
                      () => api(`${base}/addons/${a.id}`, { method: 'DELETE' }),
                      'Add-on quitado',
                    );
                  },
                })),
                ...(canReactivate && sub.status !== 'suspended'
                  ? [
                      {
                        label: 'Reactivar',
                        icon: PlayCircle,
                        onSelect: () => {
                          void reactivate();
                        },
                      },
                    ]
                  : []),
                ...(canSuspend
                  ? [
                      {
                        label: 'Suspender',
                        icon: PauseCircle,
                        destructive: true,
                        separatorBefore: true,
                        onSelect: () => {
                          setDialog('suspend');
                        },
                      },
                    ]
                  : []),
                {
                  label: 'Cancelar suscripción',
                  icon: XCircle,
                  destructive: true,
                  separatorBefore: !canSuspend,
                  onSelect: () => {
                    setDialog('cancel');
                  },
                },
              ]
            : []
        }
      />
      <ConfirmDialog
        open={dialog === 'suspend'}
        onOpenChange={(v) => {
          if (!v) setDialog(null);
        }}
        title="¿Suspender la suscripción?"
        description="Se frena lo nuevo (ingesta, cualificación y envíos). No se borran datos ni se cortan las conversaciones en curso. Se puede reactivar cuando quieras."
        confirmLabel="Suspender"
        reason={{
          label: 'Motivo (queda en el historial)',
          placeholder: 'Ej.: factura de septiembre sin pagar',
        }}
        onConfirm={(reason) =>
          actOrThrow(
            () => api(`${base}/action`, { method: 'POST', body: { action: 'suspend', reason } }),
            'Suspendida',
          )
        }
      />
      <ConfirmDialog
        open={dialog === 'cancel'}
        onOpenChange={(v) => {
          if (!v) setDialog(null);
        }}
        title="¿Cancelar la suscripción?"
        description="Deja de facturarse desde el próximo período. No se borran datos."
        confirmLabel="Cancelar suscripción"
        onConfirm={() =>
          actOrThrow(
            () => api(`${base}/action`, { method: 'POST', body: { action: 'cancel' } }),
            'Cancelada',
          )
        }
      />
      {live ? (
        <>
          <FormDialog
            title="Cambiar de plan"
            open={dialog === 'plan'}
            onOpenChange={(v) => {
              setDialog(v ? 'plan' : null);
            }}
            onSubmit={(f) =>
              actOrThrow(
                () =>
                  api(`${base}/change-plan`, {
                    method: 'POST',
                    body: { planId: fd(f, 'planId'), effective: fd(f, 'effective') },
                  }),
                'Plan actualizado',
              )
            }
          >
            <NativeSelect
              label="Plan"
              name="planId"
              options={(plans.data ?? [])
                .filter((p) => !p.archivedAt)
                .map((p) => ({
                  value: p.id,
                  label: `${p.name} (${money(p.priceCents, p.currency)})`,
                }))}
            />
            <NativeSelect
              label="Desde"
              name="effective"
              defaultValue="next"
              options={[
                { value: 'next', label: 'El próximo período' },
                { value: 'now', label: 'Ahora (ajustar la factura a mano)' },
              ]}
            />
          </FormDialog>
          <FormDialog
            title="Agregar add-on"
            open={dialog === 'addon'}
            onOpenChange={(v) => {
              setDialog(v ? 'addon' : null);
            }}
            onSubmit={(f) =>
              actOrThrow(
                () =>
                  api(`${base}/addons`, {
                    method: 'POST',
                    body: { addonId: fd(f, 'addonId'), quantity: Number(fd(f, 'quantity')) },
                  }),
                'Add-on agregado',
              )
            }
          >
            <NativeSelect
              label="Add-on"
              name="addonId"
              options={(addons.data ?? [])
                .filter((a) => !a.archivedAt)
                .map((a) => ({
                  value: a.id,
                  label: `${a.name} (${money(a.priceCents, a.currency)})`,
                }))}
            />
            <Field
              label="Cantidad"
              name="quantity"
              type="number"
              min={1}
              defaultValue={1}
              required
            />
          </FormDialog>
        </>
      ) : null}
    </>
  );
}

// ------------------------------------------------------------------ facturas

function InvoiceEditor({
  invoice,
  onChanged,
}: {
  invoice: Invoice;
  onChanged: () => Promise<unknown>;
}) {
  const tenants = useAdminList<Tenant>('tenants', '/api/admin/tenants');
  const base = `/api/admin/invoices/${invoice.id}`;
  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
      await onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  const addLine = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    void act(
      () =>
        api(`${base}/lines`, {
          method: 'POST',
          body: {
            description: fd(f, 'description'),
            quantity: Number(fd(f, 'quantity') || '1'),
            unitPriceCents: Math.round(Number(fd(f, 'price')) * 100),
            kind: fd(f, 'kind'),
            tenantId: fd(f, 'tenantId') || null,
          },
        }),
      'Línea agregada',
    ).then(() => {
      form.reset();
    });
  };
  const draft = invoice.status === 'draft';
  const [confirm, setConfirm] = useState<'paid' | 'void' | null>(null);
  const actOrThrow = async (fn: () => Promise<unknown>, ok: string) => {
    await fn();
    toast.success(ok);
    await onChanged();
  };
  return (
    <div className="flex flex-col gap-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Concepto</TableHead>
            <TableHead className="text-right">Cant.</TableHead>
            <TableHead className="text-right">Precio</TableHead>
            <TableHead className="text-right">Importe</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoice.lines.map((l) => (
            <TableRow key={l.id}>
              <TableCell>
                <span className="font-medium">{l.description}</span>{' '}
                <Badge variant="secondary">{l.kind}</Badge>
              </TableCell>
              <TableCell className="text-right">{l.quantity}</TableCell>
              <TableCell className="text-right">
                {money(l.unitPriceCents, invoice.currency)}
              </TableCell>
              <TableCell className="text-right">{money(l.amountCents, invoice.currency)}</TableCell>
              <TableCell>
                {draft ? (
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Quitar línea"
                    onClick={() =>
                      void act(
                        () => api(`${base}/lines/${l.id}`, { method: 'DELETE' }),
                        'Línea quitada',
                      )
                    }
                  >
                    <Trash2 />
                  </Button>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <dl className="ml-auto grid w-full max-w-xs grid-cols-[1fr_auto] gap-x-6 gap-y-1 rounded-lg bg-muted/40 p-3 text-sm tabular-nums">
        <dt className="text-muted-foreground">Subtotal</dt>
        <dd className="text-right">{money(invoice.subtotalCents, invoice.currency)}</dd>
        <dt className="text-muted-foreground">IVA</dt>
        <dd className="text-right">{money(invoice.taxCents, invoice.currency)}</dd>
        <dt className="border-t pt-1 font-semibold">Total</dt>
        <dd className="border-t pt-1 text-right font-display font-semibold">
          {money(invoice.totalCents, invoice.currency)}
        </dd>
        {invoice.paidCents > 0 ? (
          <>
            <dt className="text-success">Pagado</dt>
            <dd className="text-right text-success">
              {money(invoice.paidCents, invoice.currency)}
            </dd>
          </>
        ) : null}
      </dl>
      {draft ? (
        <>
          <form onSubmit={addLine} className="grid gap-2 md:grid-cols-6 md:items-end">
            <div className="md:col-span-2">
              <Field label="Concepto" name="description" required />
            </div>
            <Field label="Cant." name="quantity" type="number" min={1} defaultValue={1} />
            <Field
              label="Precio (negativo = descuento)"
              name="price"
              type="number"
              step="0.01"
              required
            />
            <NativeSelect
              label="Tipo"
              name="kind"
              defaultValue="adjustment"
              options={['adjustment', 'overage', 'addon', 'plan'].map((k) => ({
                value: k,
                label: k,
              }))}
            />
            <NativeSelect
              label="Empresa"
              name="tenantId"
              options={[
                { value: '', label: '—' },
                ...(tenants.data ?? [])
                  .filter((t) => t.clientId === invoice.clientId)
                  .map((t) => ({ value: t.id, label: t.name })),
              ]}
            />
            <Button type="submit" variant="outline" className="md:col-span-6 md:justify-self-start">
              <Plus /> Agregar línea
            </Button>
          </form>
          <FormDialog
            title={`Emitir la factura de ${invoice.clientName}`}
            submitLabel="Emitir factura"
            trigger={
              <Button>
                <Send /> Emitir
              </Button>
            }
            onSubmit={(f) =>
              act(
                () =>
                  api(`${base}/issue`, {
                    method: 'POST',
                    body: {
                      ...(fd(f, 'tax')
                        ? { taxRateBps: Math.round(Number(fd(f, 'tax')) * 100) }
                        : {}),
                      ...(fd(f, 'due') ? { dueDays: Number(fd(f, 'due')) } : {}),
                      notify: f.get('notify') === 'on',
                    },
                  }),
                'Factura emitida',
              )
            }
          >
            <p className="text-sm text-muted-foreground">
              Se le asigna el número correlativo del año y queda inmutable. Vacío = los valores de
              los datos de facturación.
            </p>
            <Field label="IVA (%)" name="tax" type="number" step="0.01" min="0" placeholder="21" />
            <Field label="Vence en (días)" name="due" type="number" min="0" placeholder="10" />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" name="notify" defaultChecked /> Avisar por email a los admins
              del cliente
            </label>
          </FormDialog>
        </>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline">
          <a href={`${base}/pdf`} target="_blank" rel="noreferrer">
            <FileText /> {draft ? 'Vista previa PDF' : 'PDF'}
          </a>
        </Button>
        {invoice.status === 'issued' || invoice.status === 'overdue' ? (
          <Button
            variant="ghost"
            onClick={() => {
              setConfirm('paid');
            }}
          >
            <HandCoins /> Marcar pagada sin pago
          </Button>
        ) : null}
        {invoice.status !== 'void' && invoice.status !== 'paid' ? (
          <Button
            variant="ghost"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => {
              setConfirm('void');
            }}
          >
            <Ban /> Anular
          </Button>
        ) : null}
      </div>
      <ConfirmDialog
        open={confirm === 'paid'}
        onOpenChange={(v) => {
          if (!v) setConfirm(null);
        }}
        destructive={false}
        title="¿Marcarla pagada sin registrar un pago?"
        description="Para bonificaciones o compensaciones. No entra dinero en los pagos; queda registrado con el motivo."
        confirmLabel="Marcar pagada"
        reason={{ label: 'Motivo', placeholder: 'Ej.: bonificación por el retraso de agosto' }}
        onConfirm={(reason) =>
          actOrThrow(
            () => api(`${base}/mark-paid`, { method: 'POST', body: { reason } }),
            'Marcada pagada',
          )
        }
      />
      <ConfirmDialog
        open={confirm === 'void'}
        onOpenChange={(v) => {
          if (!v) setConfirm(null);
        }}
        title={`¿Anular la factura ${invoice.number}?`}
        description="Queda anulada con su número (no se reutiliza). No se puede deshacer."
        confirmLabel="Anular factura"
        reason={{ label: 'Motivo de la anulación' }}
        onConfirm={(reason) =>
          actOrThrow(
            () => api(`${base}/void`, { method: 'POST', body: { reason } }),
            'Factura anulada',
          )
        }
      />
    </div>
  );
}

export function InvoicesSection() {
  const qc = useQueryClient();
  const clients = useAdminList<Client>('clients', '/api/admin/clients');
  const [clientId, setClientId] = useState('');
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const qs = new URLSearchParams({
    ...(clientId ? { clientId } : {}),
    ...(status ? { status } : {}),
  }).toString();
  const invoices = useQuery({
    queryKey: ['admin', 'invoices', qs],
    queryFn: () => api<Invoice[]>(`/api/admin/invoices${qs ? `?${qs}` : ''}`),
  });
  const cId = useId();
  const sId = useId();
  const current = invoices.data?.find((i) => i.id === open);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor={cId}>Cliente</Label>
          <select
            id={cId}
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value);
            }}
            className="h-9 rounded-md border border-input bg-card px-2 text-sm"
          >
            <option value="">Todos</option>
            {clients.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={sId}>Estado</Label>
          <select
            id={sId}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
            }}
            className="h-9 rounded-md border border-input bg-card px-2 text-sm"
          >
            <option value="">Todos</option>
            {INVOICE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {INVOICE_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
        <Button asChild variant="outline" className="ml-auto">
          <a href={`/api/admin/invoices.csv${qs ? `?${qs}` : ''}`} download>
            <Download /> CSV para el contador
          </a>
        </Button>
      </div>
      <Loadable
        isLoading={invoices.isLoading}
        error={invoices.error}
        onRetry={() => void invoices.refetch()}
      >
        <DataTable
          rows={invoices.data}
          empty="Sin facturas. Los borradores se crean solos al empezar cada período (o con “Generar factura” en Suscripciones)."
          emptyIcon={<FileText />}
          columns={[
            {
              header: 'Número',
              cell: (i) => (
                <button
                  type="button"
                  className="inline-flex items-center gap-2 font-medium text-foreground hover:text-highlight hover:underline"
                  onClick={() => {
                    setOpen(i.id);
                  }}
                >
                  <FileText className="size-4 text-muted-foreground" aria-hidden />
                  {i.number}
                </button>
              ),
            },
            {
              header: 'Cliente',
              cell: (i) => (
                <span className="inline-flex items-center gap-2">
                  <Monogram name={i.clientName} size="xs" />
                  {i.clientName}
                </span>
              ),
            },
            { header: 'Estado', cell: (i) => <InvoiceBadge status={i.status} /> },
            {
              header: 'Período',
              cell: (i) => (
                <span className="whitespace-nowrap">{fmtPeriod(i.periodStart, i.periodEnd)}</span>
              ),
            },
            {
              header: 'Vence',
              cell: (i) => <span className="whitespace-nowrap">{fmtDate(i.dueDate)}</span>,
            },
            {
              header: 'Total',
              className: 'text-right',
              cell: (i) => (
                <span className="font-medium tabular-nums">{money(i.totalCents, i.currency)}</span>
              ),
            },
            {
              header: '',
              className: 'w-24 text-right',
              cell: (i) => (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setOpen(i.id);
                  }}
                >
                  Abrir
                </Button>
              ),
            },
          ]}
        />
      </Loadable>
      <Dialog
        open={open !== null}
        onOpenChange={(v) => {
          if (!v) setOpen(null);
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader className="flex-row items-center gap-3">
            <IconBadge tone="brand" size="lg">
              <FileText />
            </IconBadge>
            <div className="flex flex-col gap-1">
              <DialogTitle>
                {current?.number} · {current?.clientName}
              </DialogTitle>
              {current ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <InvoiceBadge status={current.status} />
                  {current.periodStart ? fmtPeriod(current.periodStart, current.periodEnd) : null}
                </div>
              ) : null}
            </div>
          </DialogHeader>
          {current ? (
            <InvoiceEditor
              invoice={current}
              onChanged={() => qc.invalidateQueries({ queryKey: ['admin', 'invoices'] })}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------------------------------------------------------ pagos con comprobante

const METHOD_LABEL: Record<(typeof PAYMENT_METHODS)[number], string> = {
  bank_transfer: 'Transferencia',
  cash: 'Efectivo',
  mercadopago: 'Mercado Pago',
  stripe: 'Stripe',
  other: 'Otro',
};

export function PaymentsWithProofSection() {
  const payments = useAdminList<PaymentDetail>('payment-details', '/api/admin/payments/details');
  const clients = useAdminList<Client>('clients', '/api/admin/clients');
  const invoices = useAdminList<Invoice>('invoices', '/api/admin/invoices');
  const run = useAdminMutation(
    ['payment-details', 'invoices', 'subscription-details'],
    'Pago registrado',
  );
  const openInvoices = (invoices.data ?? []).filter(
    (i) => i.status === 'issued' || i.status === 'overdue',
  );
  return (
    <div className="flex flex-col gap-4">
      <div>
        <FormDialog
          title="Registrar pago"
          trigger={
            <Button>
              <Plus /> Registrar pago
            </Button>
          }
          onSubmit={async (f) => {
            const file = f.get('proof');
            const invoiceId = fd(f, 'invoiceId');
            const inv = openInvoices.find((i) => i.id === invoiceId);
            const proof =
              file instanceof File && file.size > 0
                ? {
                    filename: file.name,
                    mime: file.type || 'application/pdf',
                    contentBase64: await fileToBase64(file),
                  }
                : undefined;
            return run(() =>
              api('/api/admin/payments/with-proof', {
                method: 'POST',
                body: {
                  clientId: inv?.clientId ?? fd(f, 'clientId'),
                  invoiceId: invoiceId || null,
                  amountCents: Math.round(Number(fd(f, 'amount')) * 100),
                  currency: inv?.currency ?? fd(f, 'currency'),
                  method: fd(f, 'method'),
                  reference: fd(f, 'reference') || null,
                  receivedAt: fd(f, 'receivedAt'),
                  notes: fd(f, 'notes') || null,
                  ...(proof ? { proof } : {}),
                },
              }),
            );
          }}
        >
          <NativeSelect
            label="Factura"
            name="invoiceId"
            options={[
              { value: '', label: 'Pago a cuenta (sin factura)' },
              ...openInvoices.map((i) => ({
                value: i.id,
                label: `${i.number} · ${i.clientName} · ${money(i.totalCents - i.paidCents, i.currency)}`,
              })),
            ]}
          />
          <NativeSelect
            label="Cliente (si es a cuenta)"
            name="clientId"
            options={(clients.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
          />
          <Field label="Monto" name="amount" type="number" step="0.01" min="0.01" required />
          <NativeSelect
            label="Moneda (si es a cuenta)"
            name="currency"
            options={['EUR', 'USD', 'ARS'].map((c) => ({ value: c, label: c }))}
          />
          <NativeSelect
            label="Método"
            name="method"
            options={PAYMENT_METHODS.map((m) => ({ value: m, label: METHOD_LABEL[m] }))}
          />
          <Field label="Referencia (nº de transferencia)" name="reference" />
          <Field
            label="Fecha de cobro"
            name="receivedAt"
            type="date"
            defaultValue={new Date().toISOString().slice(0, 10)}
            required
          />
          <Field
            label="Comprobante (PDF o imagen)"
            name="proof"
            type="file"
            accept="application/pdf,image/png,image/jpeg"
          />
          <Field label="Notas" name="notes" />
        </FormDialog>
      </div>
      <DataTable
        rows={payments.data}
        empty="Sin pagos registrados."
        emptyIcon={<Landmark />}
        columns={[
          {
            header: 'Fecha',
            cell: (p) => <span className="whitespace-nowrap">{fmtDate(p.receivedAt)}</span>,
          },
          {
            header: 'Cliente',
            cell: (p) => {
              const name = clients.data?.find((c) => c.id === p.clientId)?.name ?? '—';
              return (
                <span className="inline-flex items-center gap-2">
                  <Monogram name={name} size="xs" />
                  {name}
                </span>
              );
            },
          },
          {
            header: 'Monto',
            className: 'text-right',
            cell: (p) => (
              <span className="font-medium tabular-nums">{money(p.amountCents, p.currency)}</span>
            ),
          },
          {
            header: 'Método',
            cell: (p) => {
              const Icon = METHOD_ICON[p.method];
              return (
                <span className="inline-flex items-center gap-2">
                  <Icon className="size-4 text-muted-foreground" aria-hidden />
                  {METHOD_LABEL[p.method]}
                </span>
              );
            },
          },
          {
            header: 'Factura',
            cell: (p) => {
              const num = invoices.data?.find((i) => i.id === p.invoiceId)?.number;
              return num ? (
                <span className="inline-flex items-center gap-1.5">
                  <FileText className="size-3.5 text-muted-foreground" aria-hidden />
                  {num}
                </span>
              ) : (
                <Badge variant="secondary">a cuenta</Badge>
              );
            },
          },
          {
            header: 'Comprobante',
            cell: (p) =>
              p.hasProof ? (
                <a
                  className="inline-flex max-w-48 items-center gap-1.5 text-highlight hover:underline"
                  href={`/api/admin/payments/${p.id}/proof`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Paperclip className="size-3.5 shrink-0" aria-hidden />
                  <span className="truncate">{p.proofFilename ?? 'ver'}</span>
                </a>
              ) : (
                <span className="text-muted-foreground">—</span>
              ),
          },
        ]}
      />
    </div>
  );
}

// ------------------------------------------------------------------ márgenes

export function MarginsSection() {
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const periodId = useId();
  const q = useQuery({
    queryKey: ['admin', 'margins', period],
    queryFn: () => api<MarginsResponse>(`/api/admin/margins?period=${period}`),
  });
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor={periodId}>Mes</Label>
          <Input
            id={periodId}
            type="month"
            value={period}
            onChange={(e) => {
              setPeriod(e.target.value);
            }}
            className="w-44"
          />
        </div>
        <p className="text-sm text-muted-foreground">
          Ingreso = facturas emitidas del mes (sin IVA). Costo = tarifas por uso cargadas en
          «costos» + fijo mensual, en la moneda de las facturas. El costo real de IA llega en
          dólares (Gemini factura en USD) y se muestra aparte, tal cual, sin convertir:{' '}
          <strong>no está restado del margen</strong>.
        </p>
      </div>
      <AiCostCard period={period} />
      <Loadable isLoading={q.isLoading} error={q.error}>
        <div className="flex flex-col gap-3">
          {q.data?.rows.map((r) => (
            <Card key={r.clientId}>
              <CardHeader className="pb-2">
                <CardTitle className="flex flex-wrap items-center gap-2 text-base">
                  <IconBadge tone={r.marginCents < 0 ? 'destructive' : 'success'} size="sm">
                    {r.marginCents < 0 ? <TrendingDown /> : <TrendingUp />}
                  </IconBadge>
                  {r.clientName}
                  <Badge variant={r.marginCents < 0 ? 'destructive' : 'success'}>
                    margen {money(r.marginCents, r.currency ?? 'EUR')}
                    {r.marginPct === null ? '' : ` (${(r.marginPct * 100).toFixed(0)} %)`}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Ingreso {money(r.revenueCents, r.currency ?? 'EUR')} · costo estimado{' '}
                  {money(r.costCents, r.currency ?? 'EUR')}
                  {r.aiUsdCents > 0 &&
                    ` · IA real ${usd(r.aiUsdCents / 100)} (en USD, no incluido en el margen)`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="flex flex-col gap-1 text-sm">
                  {r.tenants.map((t) => (
                    <li key={t.tenantId} className="flex flex-wrap gap-2">
                      <span className="font-medium">{t.tenantName}</span>
                      <span className="text-muted-foreground">
                        {money(t.costCents, r.currency ?? 'EUR')}
                        {t.aiUsdCents > 0 && ` + ${usd(t.aiUsdCents / 100)} de IA real`} ·{' '}
                        {Object.entries(t.usage)
                          .map(([k, v]) => `${USAGE_LABELS[k] ?? k}: ${fmtNum(v)}`)
                          .join(' · ') || 'sin uso registrado'}
                      </span>
                      <CostRatesDialog tenantId={t.tenantId} tenantName={t.tenantName} />
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </Loadable>
    </div>
  );
}

const usd = (n: number | null) =>
  n === null
    ? '—'
    : new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: n < 1 ? 3 : 2,
      }).format(n);
/** Nombres legibles de las métricas de uso (las claves son las de usage_counters). */
const USAGE_LABELS: Record<string, string> = {
  leads_ingested: 'ingresados',
  leads_qualified: 'cualificados',
  emails_sent: 'emails enviados',
  leads_pushed: 'pasados a Instantly',
  est_gemini: 'Gemini estimado (leads)',
  est_leadmagic: 'LeadMagic estimado (leads)',
  ai_gemini: 'Gemini real (leads)',
};
const fmtTokens = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)} M` : `${fmtNum(Math.round(n / 1000))} k`;

/**
 * Costo REAL de Gemini por empresa (tokens que factura Google, registrados por WF02 desde el
 * 2026-10-07). En USD. Los leads cualificados antes del registro no entran: se avisan aparte.
 */
function AiCostCard({ period }: { period: string }) {
  const q = useQuery({
    queryKey: ['admin', 'ai-costs', period],
    queryFn: () => api<AiCostsResponse>(`/api/admin/ai-costs?period=${period}`),
  });
  const d = q.data;
  const unmetered = d?.tenants.reduce((s, t) => s + t.leadsUnmetered, 0) ?? 0;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <IconBadge tone="neutral" size="sm">
            <Cpu />
          </IconBadge>
          Costo real de IA (Gemini)
          {d && <Badge variant="secondary">{usd(d.totalUsd)} en el mes</Badge>}
          {d?.projectedMonthUsd !== null && d?.projectedMonthUsd !== undefined && (
            <Badge variant="outline">≈ {usd(d.projectedMonthUsd)} proyectado a fin de mes</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Tokens que factura Google (incluye búsquedas y razonamiento) × precio de lista. En
          dólares, como factura Google, sin convertir a la moneda de las facturas. «Por enviado» =
          costo ÷ leads de esos que ya se pasaron a Instantly.
          {unmetered > 0 &&
            ` ${fmtNum(unmetered)} leads del mes se cualificaron antes de empezar a registrar el costo: no entran acá.`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Loadable isLoading={q.isLoading} error={q.error}>
          {d?.tenants.every((t) => t.leadsMetered === 0) ? (
            <p className="text-sm text-muted-foreground">
              Sin leads con costo registrado este mes.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Empresa</TableHead>
                  <TableHead className="text-right">Leads</TableHead>
                  <TableHead className="text-right">Costo</TableHead>
                  <TableHead className="text-right">Por lead</TableHead>
                  <TableHead className="text-right">Por aprobado</TableHead>
                  <TableHead className="text-right">Por enviado</TableHead>
                  <TableHead className="text-right">Proyección mes</TableHead>
                  <TableHead>Modelos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {d?.tenants
                  .filter((t) => t.leadsMetered > 0)
                  .map((t) => (
                    <TableRow key={t.tenantId}>
                      <TableCell className="font-medium">{t.tenantName}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtNum(t.leadsMetered)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{usd(t.costUsd)}</TableCell>
                      <TableCell className="text-right tabular-nums">{usd(t.perLeadUsd)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {usd(t.perApprovedUsd)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {usd(t.perPushedUsd)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {usd(t.projectedMonthUsd)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {t.models
                          .map(
                            (m) =>
                              `${m.model.replace('gemini-', '')}: ${fmtTokens(m.tokensIn)}→${fmtTokens(m.tokensOut)} · ${m.costUsd === null ? 'sin precio' : usd(m.costUsd)}`,
                          )
                          .join(' · ')}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </Loadable>
      </CardContent>
    </Card>
  );
}

function CostRatesDialog({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const run = useAdminMutation(['margins'], 'Costos guardados');
  return (
    <FormDialog
      title={`Costos estimados de ${tenantName}`}
      trigger={
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs">
          costos
        </Button>
      }
      onSubmit={(f) =>
        run(() =>
          api(`/api/admin/tenants/${tenantId}/cost-rates`, {
            method: 'PUT',
            body: {
              ...(fd(f, 'ingested') ? { lead_ingested_cents: Number(fd(f, 'ingested')) } : {}),
              ...(fd(f, 'qualified') ? { lead_qualified_cents: Number(fd(f, 'qualified')) } : {}),
              ...(fd(f, 'sent') ? { email_sent_cents: Number(fd(f, 'sent')) } : {}),
              ...(fd(f, 'fixed')
                ? { fixed_monthly_cents: Math.round(Number(fd(f, 'fixed')) * 100) }
                : {}),
              ...Object.fromEntries(
                PROVIDER_COSTS.filter((p) => fd(f, p.rate)).map((p) => [
                  p.rate,
                  Number(fd(f, p.rate)),
                ]),
              ),
            },
          }),
        )
      }
    >
      <p className="text-sm text-muted-foreground">
        En la moneda de las facturas: céntimos por unidad (admite decimales); el fijo, por mes.
      </p>
      <Field label="Por lead ingresado (cént.)" name="ingested" type="number" step="0.01" min="0" />
      <Field
        label="Por lead cualificado (cént.)"
        name="qualified"
        type="number"
        step="0.01"
        min="0"
      />
      <Field label="Por email enviado (cént.)" name="sent" type="number" step="0.01" min="0" />
      <Field label="Fijo mensual (€)" name="fixed" type="number" step="0.01" min="0" />
      <p className="pt-2 text-sm text-muted-foreground">
        Proveedores (estimado, céntimos por unidad). Vacío = no se calcula.
      </p>
      {PROVIDER_COSTS.map((p) => (
        <Field key={p.rate} label={p.label} name={p.rate} type="number" step="0.01" min="0" />
      ))}
      <p className="pt-2 text-sm text-muted-foreground">
        El costo real de Gemini no se carga acá: se mide solo y se muestra en dólares, sin
        convertir. La tarifa estimada de Gemini sólo se aplica a los leads cualificados antes de
        medirlo.
      </p>
    </FormDialog>
  );
}

// ------------------------------------------------------------------ datos de facturación

export function BillingSettingsSection() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['admin', 'agency-billing'],
    queryFn: () => api<BillingDetails>('/api/admin/agency/billing-details'),
  });
  const [busy, setBusy] = useState(false);
  const submit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const tax = fd(f, 'tax');
    const due = fd(f, 'due');
    setBusy(true);
    api('/api/admin/agency/billing-details', {
      method: 'PUT',
      body: {
        legalName: fd(f, 'legalName') || undefined,
        taxId: fd(f, 'taxId') || undefined,
        address: fd(f, 'address') || undefined,
        email: fd(f, 'email') || undefined,
        paymentInstructions: fd(f, 'payment') || undefined,
        invoicePrefix: fd(f, 'prefix') || undefined,
        ...(tax ? { taxRateBps: Math.round(Number(tax) * 100) } : {}),
        ...(due ? { dueDays: Number(due) } : {}),
      },
    })
      .then(() => {
        toast.success('Datos de facturación guardados');
        void qc.invalidateQueries({ queryKey: ['admin', 'agency-billing'] });
      })
      .catch((err: unknown) => toast.error(errorMessage(err)))
      .finally(() => {
        setBusy(false);
      });
  };
  const d = q.data;
  const paymentId = useId();
  const addrId = useId();
  return (
    <Loadable isLoading={q.isLoading} error={q.error}>
      {d ? (
        <Card className="max-w-3xl">
          <CardHeader>
            <CardTitle className="text-base">Emisor de las facturas (agencia)</CardTitle>
            <CardDescription>Sale en el PDF y en “Mi suscripción” de los clientes.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="grid gap-4 md:grid-cols-2">
              <Field label="Razón social" name="legalName" defaultValue={d.legalName ?? ''} />
              <Field label="NIF / CUIT" name="taxId" defaultValue={d.taxId ?? ''} />
              <Field
                label="Email de facturación"
                name="email"
                type="email"
                defaultValue={d.email ?? ''}
              />
              <Field
                label="Prefijo de numeración"
                name="prefix"
                defaultValue={d.invoicePrefix ?? 'ETW'}
                pattern="[A-Z0-9]{1,10}"
              />
              <Field
                label="IVA por defecto (%)"
                name="tax"
                type="number"
                step="0.01"
                min="0"
                defaultValue={d.taxRateBps === undefined ? '' : d.taxRateBps / 100}
              />
              <Field
                label="Vencimiento por defecto (días)"
                name="due"
                type="number"
                min="0"
                defaultValue={d.dueDays ?? 10}
              />
              <div className="flex flex-col gap-1.5 md:col-span-2">
                <Label htmlFor={addrId}>Dirección</Label>
                <Textarea id={addrId} name="address" defaultValue={d.address ?? ''} />
              </div>
              <div className="flex flex-col gap-1.5 md:col-span-2">
                <Label htmlFor={paymentId}>Cómo pagar (IBAN/CBU/alias)</Label>
                <Textarea
                  id={paymentId}
                  name="payment"
                  defaultValue={d.paymentInstructions ?? ''}
                />
              </div>
              <div className="md:col-span-2">
                <Button type="submit" disabled={busy}>
                  Guardar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : null}
    </Loadable>
  );
}
